import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveImg } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import Seo from "@/components/Seo";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, X, Star, ArrowUp, ArrowLeft, ImageIcon } from "lucide-react";

const PHV_STATUSES = ["Manchester PHV Licensed", "Manchester PHV Licence Pending", "Suitable for PHV Licensing", "Not Currently Licensed"];
const AVAIL = [["available", "Available Now"], ["coming_soon", "Coming Soon"], ["rented", "Currently Rented"], ["hidden", "Hidden"]];
const FUEL = ["Hybrid", "Petrol", "Electric", "Diesel"];
const TRANS = ["Automatic", "Manual"];

const EMPTY = {
  make: "", model: "", year: new Date().getFullYear(), registration: "", colour: "",
  fuel_type: "Hybrid", transmission: "Automatic", doors: 5, mileage: 0,
  phv_licence_status: "Not Currently Licensed", availability: "available",
  weekly_price_without_insurance: "", weekly_price_with_insurance: "", insurance_available: false,
  deposit: "", minimum_rental_weeks: 1, short_description: "", full_description: "",
  features: [], main_image: "", gallery_images: [], featured: false, show_on_homepage: true,
  published: true, seo_title: "", seo_description: "", slug: "", show_registration: false, is_demo: false,
};

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-zinc-400">{hint}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border bg-white p-6">
      <h2 className="font-heading text-lg font-bold">{title}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function VehicleForm() {
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const navigate = useNavigate();
  const { refresh } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mainRef = useRef();
  const galleryRef = useRef();

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/vehicles/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data, features: r.data.features || [] });
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onMain = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { set("main_image", await uploadFile(file)); toast.success("Main photo uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      setForm((prev) => ({ ...prev, gallery_images: [...prev.gallery_images, ...urls] }));
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const moveGalleryUp = (i) => {
    if (i === 0) return;
    setForm((prev) => {
      const g = [...prev.gallery_images];
      [g[i - 1], g[i]] = [g[i], g[i - 1]];
      return { ...prev, gallery_images: g };
    });
  };
  const removeGallery = (i) => setForm((prev) => ({ ...prev, gallery_images: prev.gallery_images.filter((_, idx) => idx !== i) }));
  const makeMain = (i) => setForm((prev) => {
    const g = [...prev.gallery_images];
    const chosen = g.splice(i, 1)[0];
    const old = prev.main_image;
    return { ...prev, main_image: chosen, gallery_images: old ? [old, ...g] : g };
  });

  const save = async (publish) => {
    if (!form.make || !form.model) { toast.error("Make and model are required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year) || new Date().getFullYear(),
      doors: Number(form.doors) || 5,
      mileage: Number(form.mileage) || 0,
      minimum_rental_weeks: Number(form.minimum_rental_weeks) || 1,
      weekly_price_without_insurance: form.weekly_price_without_insurance === "" ? null : Number(form.weekly_price_without_insurance),
      weekly_price_with_insurance: form.weekly_price_with_insurance === "" ? null : Number(form.weekly_price_with_insurance),
      deposit: form.deposit === "" ? null : Number(form.deposit),
      published: publish,
      is_demo: false,
    };
    try {
      if (isEdit) await api.put(`/admin/vehicles/${id}`, payload);
      else await api.post("/admin/vehicles", payload);
      toast.success(isEdit ? "Vehicle updated" : "Vehicle created");
      refresh();
      navigate("/admin");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <Seo title={isEdit ? "Edit Vehicle" : "Add Vehicle"} noindex path="/admin" />
      <button onClick={() => navigate("/admin")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-500 hover:text-black">
        <ArrowLeft className="h-4 w-4" /> Back to fleet
      </button>
      <h1 className="font-heading text-3xl font-black tracking-tight">{isEdit ? "Edit Vehicle" : "Add Vehicle"}</h1>

      <div className="mt-6 space-y-6" data-testid="vehicle-form">
        <Section title="Basic Details">
          <Field label="Make"><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="field-make" placeholder="Toyota" /></Field>
          <Field label="Model"><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="field-model" placeholder="Corolla Hybrid" /></Field>
          <Field label="Year"><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="field-year" /></Field>
          <Field label="Registration"><Input value={form.registration} onChange={(e) => set("registration", e.target.value)} data-testid="field-registration" /></Field>
          <Field label="Colour"><Input value={form.colour} onChange={(e) => set("colour", e.target.value)} /></Field>
          <Field label="Fuel Type">
            <Select value={form.fuel_type} onValueChange={(v) => set("fuel_type", v)}>
              <SelectTrigger data-testid="field-fuel"><SelectValue /></SelectTrigger>
              <SelectContent>{FUEL.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Transmission">
            <Select value={form.transmission} onValueChange={(v) => set("transmission", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TRANS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Doors"><Input type="number" value={form.doors} onChange={(e) => set("doors", e.target.value)} /></Field>
          <Field label="Mileage"><Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="field-mileage" /></Field>
        </Section>

        <Section title="PHV & Availability">
          <Field label="PHV Licence Status">
            <Select value={form.phv_licence_status} onValueChange={(v) => set("phv_licence_status", v)}>
              <SelectTrigger data-testid="field-phv-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PHV_STATUSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Availability">
            <Select value={form.availability} onValueChange={(v) => set("availability", v)}>
              <SelectTrigger data-testid="field-availability"><SelectValue /></SelectTrigger>
              <SelectContent>{AVAIL.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </Section>

        <Section title="Pricing">
          <Field label="Weekly Price Without Insurance (£)"><Input type="number" value={form.weekly_price_without_insurance} onChange={(e) => set("weekly_price_without_insurance", e.target.value)} data-testid="field-price-no-ins" placeholder="170" /></Field>
          <Field label="Weekly Price With Insurance (£)" hint="Leave blank if insurance is not available"><Input type="number" value={form.weekly_price_with_insurance} onChange={(e) => set("weekly_price_with_insurance", e.target.value)} data-testid="field-price-with-ins" /></Field>
          <Field label="Deposit (£)"><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} /></Field>
          <Field label="Minimum Rental (weeks)"><Input type="number" value={form.minimum_rental_weeks} onChange={(e) => set("minimum_rental_weeks", e.target.value)} /></Field>
        </Section>

        <div className="border border-border bg-white p-6">
          <h2 className="font-heading text-lg font-bold">Description</h2>
          <div className="mt-4 space-y-4">
            <Field label="Short Description"><Textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} /></Field>
            <Field label="Full Description"><Textarea rows={5} value={form.full_description} onChange={(e) => set("full_description", e.target.value)} /></Field>
            <Field label="Features (comma separated)"><Input value={(form.features || []).join(", ")} onChange={(e) => set("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Bluetooth, Air Conditioning, Apple CarPlay" /></Field>
          </div>
        </div>

        {/* Images */}
        <div className="border border-border bg-white p-6">
          <h2 className="font-heading text-lg font-bold">Images {uploading && <Loader2 className="ml-2 inline h-4 w-4 animate-spin" />}</h2>
          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Main Photo</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="relative h-28 w-40 overflow-hidden rounded border border-border bg-surface">
                  {form.main_image ? <img src={resolveImg(form.main_image)} alt="main" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-zinc-300"><ImageIcon className="h-8 w-8" /></div>}
                </div>
                <div>
                  <input ref={mainRef} type="file" accept="image/*" onChange={onMain} className="hidden" data-testid="upload-main" />
                  <button type="button" onClick={() => mainRef.current.click()} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-surface"><Upload className="h-4 w-4" /> Upload Main</button>
                  {form.main_image && <button type="button" onClick={() => set("main_image", "")} className="ml-2 text-sm text-red-600">Remove</button>}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Gallery Photos</Label>
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={onGallery} className="hidden" data-testid="upload-gallery" />
              <button type="button" onClick={() => galleryRef.current.click()} className="mt-2 inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-surface"><Upload className="h-4 w-4" /> Add Gallery</button>
              <div className="mt-3 flex flex-wrap gap-3">
                {form.gallery_images.map((img, i) => (
                  <div key={i} className="group relative h-20 w-28 overflow-hidden rounded border border-border">
                    <img src={resolveImg(img)} alt={`gallery ${i}`} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                      <button type="button" onClick={() => makeMain(i)} title="Make main" className="rounded bg-white p-1"><Star className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => moveGalleryUp(i)} title="Move up" className="rounded bg-white p-1"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => removeGallery(i)} title="Remove" className="rounded bg-white p-1 text-red-600"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Section title="SEO">
          <Field label="SEO Title" hint="Auto-generated if left blank"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
          <Field label="Slug" hint="Auto-generated if left blank"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
          <div className="sm:col-span-2"><Field label="Meta Description" hint="Auto-generated if left blank"><Textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></Field></div>
        </Section>

        <div className="border border-border bg-white p-6">
          <h2 className="font-heading text-lg font-bold">Options</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[
              ["featured", "Featured vehicle"],
              ["show_on_homepage", "Show on homepage"],
              ["insurance_available", "Insurance available"],
              ["show_registration", "Show registration publicly"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center justify-between rounded-lg border border-border p-4">
                <span className="text-sm font-semibold">{l}</span>
                <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
              </label>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 flex gap-3 border-t border-border bg-white p-4">
          <button onClick={() => save(true)} disabled={saving} data-testid="save-vehicle" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#00E676] px-8 font-bold text-black disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Vehicle
          </button>
          <button onClick={() => save(false)} disabled={saving} data-testid="save-draft" className="inline-flex h-12 items-center rounded-full border border-border px-8 font-bold disabled:opacity-60">
            Save as Draft
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
